import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { planningToolOptions } from 'modules/time_planning/lib/planningToolOptions'
import PlanningVersionsApi from 'modules/time_planning/store/collections/planningVersions'
import { PLANNING_TOOL } from 'modules/time_planning/store/models/PlanningVersion'
import schedules from 'modules/work_schedules/store/collections/schedules'

export type IdsType = number[]

interface Props {
  onSubmit: () => void
  onClose: () => void
}

const useForm = () =>
  React.useRef(
    new Form(
      {
        planning_tool: undefined,
        schedule_id: undefined,
      },
      {
        planning_tool: 'string',
        schedule_id: 'number',
      }
    )
  ).current

const UninstallShiftManagement = ({ onSubmit, onClose }: Props) => {
  const form = useForm()
  const hasShiftManagement = false
  const workScheduleTool = PLANNING_TOOL.workSchedule
  const planningToolValue = form.get('planning_tool').value
  const isWorkScheduleTool = planningToolValue === workScheduleTool

  const { loading: loadingResources } = useAsyncData(() => schedules.fetch(), [])

  const scheduleOptions = schedules.unarchived.map((schedule) => ({
    label: schedule.attributes.name,
    value: schedule.attributes.id,
  }))

  const { running: submitting, performAction: onClick } = useAsyncAction(
    async () => {
      await PlanningVersionsApi.rpc('deactivate', {
        effective_at: dateToString(moment(), 'backend'),
        planning_tool: planningToolValue,
        schedule_id: isWorkScheduleTool ? form.get('schedule_id').value : undefined,
      })

      onClose()
      onSubmit()
    },
    {
      successFlash: true,
      errorFlash: false,
    }
  )

  const loading = loadingResources || submitting
  const disabled = !form.isDirty || (isWorkScheduleTool && !form.get('schedule_id').isDirty)

  return (
    <Modal
      illustration="uninstall"
      title={i18n.t('app_store_v2.common.uninstall.title', {
        app_name: i18n.t('app_store_v2.apps.shift_management.title'),
      })}
      description={i18n.t('app_store_v2.common.uninstall.description')}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={disabled} onClick={onClick}>
            {i18n.t('app_store_v2.common.uninstall.submit')}
          </Button>
        </>
      }
    >
      {loading && <Loading />}
      {!loading && (
        <Box>
          <Text.Normal size="300">
            {i18n.t('time_planning.planning_versions.uninstall.description')}
          </Text.Normal>
          <Box paddingTop="s24">
            <Select
              none={i18n.t('common.select_one')}
              field={form.get('planning_tool')}
              label={i18n.t('time_planning.planning_versions.forms.planning_tool')}
              options={planningToolOptions(hasShiftManagement)}
            />
          </Box>
          <Box paddingTop="s24">
            {isWorkScheduleTool && (
              <Select
                none={i18n.t('common.select_one')}
                field={form.get('schedule_id')}
                label={i18n.t('time_planning.planning_versions.forms.schedule_id')}
                options={scheduleOptions}
              />
            )}
          </Box>
        </Box>
      )}
    </Modal>
  )
}

export default observer(UninstallShiftManagement)
